import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, CheckCircle2, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { toast } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";

const TIERS = [20, 50, 100, 200, 500] as const;
const CUSTOM_MIN_YUAN = 10;

type PayType = "alipay";

type Order = {
  order_id: string;
  out_trade_no: string;
  pay_type: PayType | "wxpay" | "wechat_h5";
  amount_yuan: number;
  credits: number;
  status: "pending" | "paid" | "failed" | "canceled";
  // Modern fields (ZPay)
  pay_url: string | null;
  qr_code_text: string | null;
  qr_code_img: string | null;
  // Legacy WeChat-only field — kept for back-compat with older responses.
  mweb_url: string | null;
  paid_at: string | null;
};

// Best-effort mobile detection. Mobile users complete payment inside the
// native payment app, so we send them to `pay_url` directly.
// Desktop users scan a QR code with their phone, so we render the image.
function isMobileUA(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

export function RechargeModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [order, setOrder] = useState<Order | null>(null);
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    if (!open) setOrder(null);
  }, [open]);

  const create = useMutation({
    mutationFn: ({ yuan, payType }: { yuan: number; payType: PayType }) =>
      apiFetch<Order>("/api/v2/billing/orders", {
        method: "POST",
        body: { yuan, pay_type: payType },
      }),
    onSuccess: (data) => setOrder(data),
    onError: (err: Error) => toast(err.message),
  });

  // Poll order status every 2 s while pending.
  const status = useQuery({
    queryKey: ["billing", "order", order?.order_id],
    enabled: !!order && order.status === "pending",
    queryFn: () => apiFetch<Order>(`/api/v2/billing/orders/${order!.order_id}`),
    refetchInterval: 2000,
  });

  useEffect(() => {
    if (!status.data || status.data.status !== "paid") return;
    setOrder(status.data);
    toast(`充值成功，到账 ${status.data.credits} 积分`);

    // Optimistic auth-store update so the Topbar balance bumps immediately
    // (the diamond chip uses zustand.user.credit_balance, not React Query).
    // We read via getState() to avoid putting `user` in the dep list and
    // re-firing this effect.
    const current = useAuthStore.getState().user;
    if (current) {
      setUser({
        ...current,
        credit_balance: current.credit_balance + status.data.credits,
      });
    }

    // Then reconcile with server truth. ["me"] is the one that drives the
    // auth store via AppLayout's setUser effect — invalidating it was missed
    // in the first cut and is exactly why the Topbar stayed stale until F5.
    qc.invalidateQueries({ queryKey: ["me"] });
    qc.invalidateQueries({ queryKey: ["billing", "balance"] });
    qc.invalidateQueries({ queryKey: ["transactions"] });
  }, [status.data, qc, setUser]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogTitle className="text-lg font-medium">充值积分</DialogTitle>
        {!order ? (
          <PickAmountView
            isPending={create.isPending}
            onPick={(yuan, payType) => create.mutate({ yuan, payType })}
          />
        ) : order.status === "paid" ? (
          <PaidView order={order} onDone={() => onOpenChange(false)} />
        ) : (
          <PendingView order={order} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PickAmountView({
  isPending,
  onPick,
}: {
  isPending: boolean;
  onPick: (yuan: number, payType: PayType) => void;
}) {
  const [selected, setSelected] = useState<number | "custom" | null>(null);
  const [customYuan, setCustomYuan] = useState("");
  const customAmount = Number(customYuan);
  const yuan =
    selected === "custom"
      ? Number.isInteger(customAmount) && customAmount >= CUSTOM_MIN_YUAN
        ? customAmount
        : null
      : selected;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        10 积分 = ¥1。
      </p>

      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          选择金额
        </p>
        <div className="grid grid-cols-3 gap-2">
          {TIERS.map((y) => (
            <button
              key={y}
              type="button"
              disabled={isPending}
              onClick={() => setSelected(y)}
              className={cn(
                "flex flex-col items-center justify-center gap-1 rounded-md border px-3 py-3 text-sm transition-colors",
                selected === y
                  ? "border-accent bg-accent/10"
                  : "border-border bg-card hover:border-accent/60",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <span className="font-mono text-base font-semibold text-foreground">
                ¥{y}
              </span>
              <span className="text-xs text-muted-foreground">{y * 10} 积分</span>
            </button>
          ))}
          <button
            type="button"
            disabled={isPending}
            onClick={() => setSelected("custom")}
            className={cn(
              "flex flex-col items-center justify-center gap-1 rounded-md border px-3 py-3 text-sm transition-colors",
              selected === "custom"
                ? "border-accent bg-accent/10"
                : "border-border bg-card hover:border-accent/60",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <span className="text-base font-semibold text-foreground">自定义</span>
            <span className="text-xs text-muted-foreground">最低 ¥10</span>
          </button>
        </div>
      </div>

      {selected === "custom" && (
        <div>
          <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
            自定义金额
          </p>
          <Input
            type="number"
            min={CUSTOM_MIN_YUAN}
            step={1}
            inputMode="numeric"
            value={customYuan}
            disabled={isPending}
            placeholder="输入不低于 10 的整数金额"
            onChange={(e) => setCustomYuan(e.target.value)}
          />
          {customYuan && !yuan && (
            <p className="mt-1 text-xs text-destructive">
              自定义金额最低 ¥10，且需为整数。
            </p>
          )}
        </div>
      )}

      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          支付方式
        </p>
        <button
          type="button"
          disabled={isPending}
          className="flex w-full items-center justify-between rounded-md border border-accent bg-accent/10 px-3 py-3 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="font-medium text-foreground">支付宝</span>
          <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
            默认
          </span>
        </button>
      </div>

      <Button
        className="w-full"
        size="lg"
        disabled={!yuan || isPending}
        onClick={() => yuan && onPick(yuan, "alipay")}
      >
        {isPending ? "正在下单…" : yuan ? `确认充值 ¥${yuan}` : "请先选金额"}
      </Button>
    </div>
  );
}

function PendingView({ order }: { order: Order }) {
  const mobile = useMemo(() => isMobileUA(), []);
  // pay_url is the universal redirect URL. Fall back to legacy mweb_url
  // when responding to old WeChat-only orders.
  const url = order.pay_url || order.mweb_url || "";
  const qrImg = order.qr_code_img || "";
  const payTypeLabel = order.pay_type === "wxpay" ? "微信支付" : "支付宝";

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          待支付 · {payTypeLabel}
        </p>
        <p className="mt-1 font-mono text-2xl font-semibold tabular-nums text-accent">
          ¥{order.amount_yuan}
        </p>
        <p className="text-xs text-muted-foreground">
          支付成功后到账 {order.credits} 积分
        </p>
      </div>

      {mobile ? (
        // Mobile users: jump to the native payment app.
        <MobilePayCard payTypeLabel={payTypeLabel} url={url} />
      ) : (
        // Desktop users: render QR only.
        <DesktopPayCard
          payTypeLabel={payTypeLabel}
          qrImg={qrImg}
        />
      )}

      <p className="text-center text-xs text-muted-foreground/70">
        正在等待支付结果 · 每 2 秒自动检查一次
      </p>
    </div>
  );
}

function MobilePayCard({
  payTypeLabel,
  url,
}: {
  payTypeLabel: string;
  url: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-md border border-border bg-card p-4">
      <Smartphone className="h-8 w-8 text-accent" />
      <p className="text-center text-sm text-foreground">
        点击下方按钮，将跳转到{payTypeLabel}完成支付
      </p>
      <Button asChild className="w-full" size="lg">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="mr-1 h-4 w-4" />
          打开{payTypeLabel}
        </a>
      </Button>
    </div>
  );
}

function DesktopPayCard({
  payTypeLabel,
  qrImg,
}: {
  payTypeLabel: string;
  qrImg: string;
}) {
  return (
    <div className="space-y-3 rounded-md border border-border bg-card p-4">
      {qrImg ? (
        <div className="flex flex-col items-center gap-2">
          <img
            src={qrImg}
            alt={`${payTypeLabel}二维码`}
            className="h-48 w-48 rounded-md border border-border bg-white object-contain p-2"
            onError={(e) => {
              // QR image failed to load (rare); hide the broken image.
              (e.currentTarget as HTMLElement).style.display = "none";
            }}
          />
          <p className="text-center text-xs text-muted-foreground">
            用{payTypeLabel}扫码完成支付
          </p>
        </div>
      ) : (
        <div className="grid min-h-48 place-items-center text-sm text-muted-foreground">
          二维码生成中…
        </div>
      )}
    </div>
  );
}

function PaidView({ order, onDone }: { order: Order; onDone: () => void }) {
  return (
    <div className="space-y-4 text-center">
      <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
      <div>
        <p className="text-lg font-semibold">充值成功</p>
        <p className="text-sm text-muted-foreground">
          +{order.credits} 积分已到账
        </p>
      </div>
      <Button onClick={onDone} className="w-full" size="lg">
        完成
      </Button>
    </div>
  );
}
